// global "active season" — the season the whole app is currently viewing. defaults to the
// server's current (date-based) season; a topbar selector lets the user switch to another
// defined season (e.g. next season 26-27) to view/edit it now. every season-scoped data hook
// reads this so switching re-keys the React query cache and refetches automatically.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext.js';

const LS_KEY = 'activeSeason';

interface SeasonValue {
  activeSeason: string; // a valid season id once `me` has loaded ('' before that)
  currentSeason: string; // the server default (date-based) season id
  seasons: string[]; // all selectable season ids
  setActiveSeason: (id: string) => void;
  isCurrent: boolean; // true when viewing the default season
}

const Ctx = createContext<SeasonValue | null>(null);

export function SeasonProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const serverCurrent = me?.season ?? '';
  const seasons = me?.seasons ?? (serverCurrent ? [serverCurrent] : []);
  // guarantee currentSeason is always a member of `seasons`. the server's current id is date-based
  // and unbounded (seasonIdFor), so once the date rolls past the last defined season it stops being
  // in the list; mirror the worker's fallback (mappings.ts currentSeason() → last defined season) so
  // the topbar <select> value always matches one of its options and never disagrees with the server.
  const currentSeason = seasons.includes(serverCurrent) ? serverCurrent : seasons[seasons.length - 1] ?? serverCurrent;

  const [stored, setStored] = useState<string | null>(() => localStorage.getItem(LS_KEY));

  // effective active season: the stored override if it's still a valid season, else the default.
  const activeSeason = useMemo(() => {
    if (stored && seasons.includes(stored)) return stored;
    return currentSeason;
  }, [stored, seasons, currentSeason]);

  // drop a stale stored id (e.g. a season that no longer exists) so it can't strand the user.
  useEffect(() => {
    if (stored && seasons.length > 0 && !seasons.includes(stored)) {
      localStorage.removeItem(LS_KEY);
      setStored(null);
    }
  }, [stored, seasons]);

  const setActiveSeason = (id: string) => {
    // selecting the default clears the override — so a user never gets pinned to a season that
    // later stops being "current" once the season rolls over on 1 sept.
    if (id === currentSeason) {
      localStorage.removeItem(LS_KEY);
      setStored(null);
    } else {
      localStorage.setItem(LS_KEY, id);
      setStored(id);
    }
  };

  const value: SeasonValue = {
    activeSeason,
    currentSeason,
    seasons,
    setActiveSeason,
    isCurrent: activeSeason === currentSeason,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSeason(): SeasonValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSeason must be used within <SeasonProvider>');
  return v;
}

/** the active-season id for data-hook query keys + URLs. '' before `me` loads (server then
 *  resolves the current season), so this never throws even outside the provider. */
export function useActiveSeason(): string {
  return useContext(Ctx)?.activeSeason ?? '';
}
